import React, { useEffect, useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Container from '@material-ui/core/Container'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'

import Table from 'components/table/SimpleTable'
import Form from 'components/form/Dialog'
import Window from 'components/dialog/Window'

import snackbarActions from 'store/modules/snackbar'
import authActions from 'store/modules/auth'
import authSelectors from 'store/selectors/auth'
import secretsActions from 'store/modules/secrets'
import secretsSelectors from 'store/selectors/secrets'

const FIELDS = [{
  title: 'Name',
  name: 'name',
}]

const SCHEMA = [{
  id: 'name',
  title: 'Name',
  helperText: 'Enter the name of the secret - this will be the name of the environment variable we inject',
  validate: {
    type: 'string',
    methods: [
      ['required', 'The name is required'],
    ]
  }
}, {
  id: 'value',
  title: 'Value',
  helperText: 'Enter the value of the secret - this will be the value of the environment variable we inject',
  component: 'textarea',
  rows: 6,
  validate: {
    type: 'string',
    methods: [
      ['required', 'The value is required'],
    ]
  }
}]

const handlers = {
  validate: (values) => {
    const errors = {}
    if(!values.name || !values.name.match(/^\w+$/)) {
      errors.name = 'name can only contain alpha-numeric characters'
    }
    return errors
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(5),
  },
  dialogPaper: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  button: {
    marginRight: theme.spacing(2),
  },
  tokenWrapper: {
    width: '100%',
    backgroundColor: "#002b36",
    color: '#fdf6e3',
    border: '1px solid #000',
    overflowX: 'auto',
  },
  tokenPre: {
    paddingLeft: theme.spacing(2),
  },
  tokenCode: {
  },
  string: {
    color: "#b58900",
  },
  comment: {
    color: "#839496",
  },
  key: {
    color: "#859900",
  },
  addButtonContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  }
}))

const Secrets = ({

}) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [ editSecret, setEditSecret ] = useState(null)
  const [ deleteSecret, setDeleteSecret ] = useState(null)

  const data = useSelector(secretsSelectors.data)

  const tableData = useMemo(() => {
    return data.map(secret => {
      return {
        id: secret.id,
        name: secret.name,
        value: secret.value,
      }
    })
  }, [
    data,
  ])

  const getActions = useCallback((item) => {
    return (
      <>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ (e) => {
            setEditSecret(item)
            return false
          }}
        >
          Edit
        </Button>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ (e) => {
            setDeleteSecret(item)
            return false
          }}
        >
          Delete
        </Button>
      </>
    )
  }, [])

  const submitForm = useCallback(async (data) => {
    const submitData = Object.assign({}, data)
    const id = submitData.id
    delete(submitData.id)
    if(id == 'new') {
      const result = await dispatch(secretsActions.create(submitData))
      if(result) {
        setEditSecret(null)
      }
    }
    else {
      const result = await dispatch(secretsActions.update(id, submitData))
      if(result) {
        setEditSecret(null)
      }
    }
  }, [])

  const onConfirmDelete = useCallback(async () => {
    const result = await dispatch(secretsActions.delete(deleteSecret.id))
    if(result) {
      setDeleteSecret(null)
    }
  }, [
    deleteSecret,
  ])
  
  useEffect(() => {
    dispatch(secretsActions.list())
  }, [])

  return (
    <Container fixed maxWidth="md">
      <Paper className={ classes.paper }>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Team Secrets
            </Typography>
            <Typography variant="body1" gutterBottom>
              These values will be injected into your build and test vms as environment variables
            </Typography>
            <div className={ classes.addButtonContainer }>
              <Button
                className={ classes.actionButton }
                size="small"
                color="secondary"
                variant="outlined"
                onClick={ (e) => {
                  setEditSecret({
                    id: 'new',
                    name: '',
                    value: '',
                  })
                  return false
                }}
              >
                Add Secret
              </Button>
            </div>
            
          </Grid>
          <Grid item xs={12}>
            <Table
              data={ tableData }
              fields={ FIELDS }
              getActions={ getActions }
            />
          </Grid>
        </Grid>
      </Paper>
      {
        editSecret && (
          <Form
            schema={ SCHEMA }
            initialValues={ editSecret }
            handlers={ handlers }
            withCancel
            onSubmit={ submitForm }
            onCancel={ () => setEditSecret(null) }
          />
        )
      }
      {
        deleteSecret && (
          <Window
            open
            title="Delete Secret?"
            size="sm"
            withCancel
            submitTitle="Delete"
            onCancel={ () => setDeleteSecret(null) }
            onSubmit={ onConfirmDelete }
          >
            <Typography variant="body1" gutterBottom>
              Are you absolutely sure you want to delete the { deleteSecret.name } secret?
            </Typography>
          </Window>
        )
      }
    </Container>
  )
}

export default Secrets
